import { useCallback, useEffect, useMemo, useState } from 'react'
import { PlusIcon, XIcon, ChartDonutIcon, ChartBarIcon } from '@phosphor-icons/react'
import type { PlotMouseEvent } from 'plotly.js'
import { cn } from '@/lib/utils'
import { ChartRenderer } from '@/components/chart/plotly-chart'
import { AddToChatContextButton } from '@/components/chat/add-to-chat-context-button'
import { useChatContext, useChatContextActions } from '@/components/chat/chat-context'
import type { ExcursionMetricsData } from '@/api/use-excursion-metrics-query'
import {
  buildProductMetricsChartFigure,
  productMetricsChartLabels,
} from '@/pages/dashboard/product-metrics-chart'

const ACTIVE_CHARTS_KEY = 'dashboardActiveCharts'

export type ChartFilterState = {
  severities: string[]
  products: string[]
}

export const EMPTY_CHART_FILTERS: ChartFilterState = { severities: [], products: [] }

export function hasChartFilters(filters: ChartFilterState): boolean {
  return filters.severities.length > 0 || filters.products.length > 0
}

/**
 * `GET /excursions` free-text query: table search wins; one chart product uses `query`.
 * Multiple chart products are narrowed on the client (main API has no product[] filter).
 */
export function excursionsApiQueryFromChartFilters(
  debouncedExcursionSearch: string,
  chartFilters: ChartFilterState,
): string | undefined {
  const search = debouncedExcursionSearch.trim()
  if (search) return search
  if (chartFilters.products.length === 1) return chartFilters.products[0]
  return undefined
}

export function usesClientSideProductChartFilter(chartFilters: ChartFilterState): boolean {
  return chartFilters.products.length > 1
}

/** Severity labels with excursions in the current metrics scope (matches the donut chart). */
export function severityChartLabels(metrics: ExcursionMetricsData): string[] {
  const counts: Record<string, number> = {
    High: metrics.by_severity.HIGH,
    Medium: metrics.by_severity.MEDIUM,
    Low: metrics.by_severity.LOW,
  }
  return Object.keys(counts).filter((label) => counts[label]! > 0)
}

/** When every selectable value in a dimension is active, treat it as no filter. */
function collapseDimensionIfAllSelected(
  selected: string[],
  selectable: string[] | undefined,
): string[] {
  if (!selectable || selectable.length <= 1) return selected
  const allSelected =
    selectable.length > 0 && selectable.every((value) => selected.includes(value))
  return allSelected ? [] : selected
}

export function normalizeChartFilters(
  filters: ChartFilterState,
  metrics: ExcursionMetricsData,
): ChartFilterState {
  return {
    severities: collapseDimensionIfAllSelected(
      filters.severities,
      severityChartLabels(metrics),
    ),
    products: collapseDimensionIfAllSelected(
      filters.products,
      productMetricsChartLabels(metrics),
    ),
  }
}

export function toggleSeverityChartFilter(
  filters: ChartFilterState,
  label: string,
  selectableLabels?: string[],
): ChartFilterState {
  const has = filters.severities.includes(label)
  const severities = has
    ? filters.severities.filter((s) => s !== label)
    : [...filters.severities, label]
  return {
    ...filters,
    severities: collapseDimensionIfAllSelected(severities, selectableLabels),
  }
}

export function toggleProductChartFilter(
  filters: ChartFilterState,
  productName: string,
  selectableProducts?: string[],
): ChartFilterState {
  const has = filters.products.includes(productName)
  const products = has
    ? filters.products.filter((p) => p !== productName)
    : [...filters.products, productName]
  return {
    ...filters,
    products: collapseDimensionIfAllSelected(products, selectableProducts),
  }
}

export function removeSeverityChartFilter(
  filters: ChartFilterState,
  label: string,
): ChartFilterState {
  return { ...filters, severities: filters.severities.filter((s) => s !== label) }
}

export function removeProductChartFilter(
  filters: ChartFilterState,
  productName: string,
): ChartFilterState {
  return { ...filters, products: filters.products.filter((p) => p !== productName) }
}

export type ExcursionTableFooterCounts = {
  /** Rows currently shown in the table (after chart client-side product filter). */
  visibleOnPage: number
  /** Total matching excursions from the API when chart filters are fully server-side. */
  filteredTotal: number | null
  pageCount: number
}

/** Footer totals for the excursions table; omits API total when product chart filter is client-only. */
export function getExcursionTableFooterCounts(
  chartFilters: ChartFilterState,
  visibleOnPage: number,
  serverPagination?: { total_items: number; total_pages: number } | null,
): ExcursionTableFooterCounts {
  const pageCount = serverPagination?.total_pages ?? 0
  if (usesClientSideProductChartFilter(chartFilters)) {
    return { visibleOnPage, filteredTotal: null, pageCount }
  }
  return {
    visibleOnPage,
    filteredTotal: serverPagination?.total_items ?? 0,
    pageCount,
  }
}

export type ChartId = 'severity-distribution' | 'impact-by-product'

export interface WorkflowChartCatalogItem {
  id: ChartId
  label: string
  icon: React.ElementType
}

export const WORKFLOW_CHART_CATALOG: WorkflowChartCatalogItem[] = [
  { id: 'severity-distribution', label: 'Severity Distribution', icon: ChartDonutIcon },
  { id: 'impact-by-product', label: 'Product metrics', icon: ChartBarIcon },
]

const ALL_CHART_IDS: ChartId[] = WORKFLOW_CHART_CATALOG.map((c) => c.id)

function getStoredActiveCharts(): ChartId[] {
  if (typeof window === 'undefined') return ALL_CHART_IDS
  try {
    const stored = localStorage.getItem(ACTIVE_CHARTS_KEY)
    if (!stored) return ALL_CHART_IDS
    const parsed: unknown = JSON.parse(stored)
    if (!Array.isArray(parsed)) return ALL_CHART_IDS
    return parsed.filter((v): v is ChartId => ALL_CHART_IDS.includes(v as ChartId))
  } catch {
    return ALL_CHART_IDS
  }
}

/** Reads legacy per-dashboard chart selection (used for migration into workflow layout storage). */
export function readLegacyDashboardChartIds(): ChartId[] {
  return getStoredActiveCharts()
}

const SEVERITY_COLORS: Record<string, string> = {
  High: '#ef4444',
  Medium: '#f59e0b',
  Low: '#10b981',
  Unknown: '#9ca3af',
}

const INACTIVE_SEVERITY_SLICE_COLOR = '#e5e7eb'

const CHART_LAYOUT_BASE: Record<string, unknown> = {
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
  font: { family: 'CircularXX, system-ui, sans-serif', size: 14, color: '#535353' },
  margin: { t: 50, r: 20, b: 44, l: 52 },
  autosize: true,
  hoverlabel: {
    bgcolor: '#262626',
    font: { color: '#fafafa', size: 12, family: 'CircularXX, system-ui, sans-serif' },
    bordercolor: 'transparent',
  },
}

const CHART_CONFIG = {
  displayModeBar: false,
  responsive: true,
  scrollZoom: false,
  doubleClick: false,
} as const

/** Left padding for plot titles (workflow dashboard charts). */
const CHART_TITLE_PAD_LEFT_PX = 16

function dashboardChartTitle(text: string, options?: { padBottom?: number }) {
  return {
    text,
    font: { size: 20, color: '#1d1d1d', weight: 500, family: 'CircularXX, system-ui, sans-serif' },
    x: 0,
    xanchor: 'left' as const,
    pad: {
      l: CHART_TITLE_PAD_LEFT_PX,
      ...(options?.padBottom != null ? { b: options.padBottom } : {}),
    },
  }
}

function getPointLabel(point: PlotMouseEvent['points'][number]): string | undefined {
  if ('label' in point && typeof point.label === 'string') return point.label
  return undefined
}

function getPointX(point: PlotMouseEvent['points'][number]): string | undefined {
  const x = point.x
  if (typeof x === 'string') return x
  return undefined
}

function severitySliceColors(labels: string[], activeSeverities: string[] | undefined): string[] {
  const filtering = (activeSeverities?.length ?? 0) > 0
  return labels.map((label) => {
    const base = SEVERITY_COLORS[label] ?? '#9ca3af'
    if (!filtering || activeSeverities!.includes(label)) return base
    return INACTIVE_SEVERITY_SLICE_COLOR
  })
}

function buildSeverityChartFromMetrics(
  metrics: ExcursionMetricsData,
  activeSeverities?: string[],
) {
  const counts: Record<string, number> = {
    High: metrics.by_severity.HIGH,
    Medium: metrics.by_severity.MEDIUM,
    Low: metrics.by_severity.LOW,
    Unknown: 0,
  }

  const labels = Object.keys(counts).filter((k) => counts[k]! > 0)
  const values = labels.map((k) => counts[k]!)

  if (labels.length === 0) {
    return {
      data: [
        {
          type: 'pie' as const,
          labels: ['No excursions'],
          values: [1],
          hole: 0.6,
          marker: {
            colors: ['#e5e7eb'],
            line: { color: '#ffffff', width: 2 },
          },
          textinfo: 'label' as const,
          textposition: 'inside' as const,
          hovertemplate: 'No severity data<extra></extra>',
          sort: false,
          pull: [0],
          direction: 'clockwise' as const,
        },
      ],
      layout: {
        ...CHART_LAYOUT_BASE,
        title: dashboardChartTitle('Severity Distribution', { padBottom: 16 }),
        showlegend: true,
        legend: {
          orientation: 'v' as const,
          y: 0.5,
          x: 1.15,
          xanchor: 'left' as const,
          yanchor: 'middle' as const,
          font: { size: 14, color: '#535353', family: 'Inter, system-ui, sans-serif', weight: 500 },
          itemsizing: 'constant' as const,
          tracegroupgap: 20,
        },
        margin: { ...(CHART_LAYOUT_BASE.margin as object), l: 20, r: 180, t: 60 },
      },
    }
  }

  const colors = severitySliceColors(labels, activeSeverities)
  const filtering = (activeSeverities?.length ?? 0) > 0
  const pull = labels.map((label) =>
    filtering && activeSeverities!.includes(label) ? 0.04 : 0,
  )

  return {
    data: [
      {
        type: 'pie' as const,
        labels,
        values,
        hole: 0.6,
        marker: {
          colors,
          line: { color: '#ffffff', width: 2 },
        },
        textinfo: 'label+percent' as const,
        textposition: 'outside' as const,
        hovertemplate: '%{label}: %{value} excursions (%{percent})<extra></extra>',
        sort: false,
        pull,
        direction: 'clockwise' as const,
      },
    ],
    layout: {
      ...CHART_LAYOUT_BASE,
      title: dashboardChartTitle('Severity Distribution', { padBottom: 16 }),
      showlegend: true,
      legend: {
        orientation: 'v' as const,
        y: 0.5,
        x: 1.15,
        xanchor: 'left' as const,
        yanchor: 'middle' as const,
        font: { size: 14, color: '#535353', family: 'Inter, system-ui, sans-serif', weight: 500 },
        itemsizing: 'constant' as const,
        tracegroupgap: 20,
      },
      margin: { ...(CHART_LAYOUT_BASE.margin as object), l: 20, r: 180, t: 60 },
    },
  }
}

function buildChart(chartId: ChartId, metrics: ExcursionMetricsData, filters: ChartFilterState) {
  switch (chartId) {
    case 'severity-distribution':
      return buildSeverityChartFromMetrics(metrics, filters.severities)
    case 'impact-by-product':
      return buildProductMetricsChartFigure(metrics, CHART_LAYOUT_BASE, dashboardChartTitle, {
        activeProducts: filters.products,
      })
  }
}

interface DashboardChartsProps {
  metrics: ExcursionMetricsData
  chartFilters: ChartFilterState
  onChartFiltersChange: (filters: ChartFilterState) => void
  disabled?: boolean
  className?: string
}

export function DashboardCharts({
  metrics,
  chartFilters,
  onChartFiltersChange,
  disabled = false,
  className,
}: DashboardChartsProps) {
  const [activeCharts, setActiveCharts] = useState(getStoredActiveCharts)
  const [pickerOpen, setPickerOpen] = useState(false)

  const updateActiveCharts = useCallback((next: ChartId[]) => {
    setActiveCharts(next)
    try {
      localStorage.setItem(ACTIVE_CHARTS_KEY, JSON.stringify(next))
    } catch {
      /* ignore */
    }
  }, [])

  const removeChart = useCallback(
    (chartId: ChartId) => {
      updateActiveCharts(activeCharts.filter((id) => id !== chartId))
    },
    [activeCharts, updateActiveCharts],
  )

  const toggleChart = useCallback(
    (chartId: ChartId) => {
      if (disabled) return
      if (activeCharts.includes(chartId)) {
        updateActiveCharts(activeCharts.filter((id) => id !== chartId))
      } else {
        updateActiveCharts([...activeCharts, chartId])
      }
    },
    [activeCharts, disabled, updateActiveCharts],
  )

  const closeChartPicker = useCallback(() => {
    setPickerOpen(false)
  }, [])

  useEffect(() => {
    if (!pickerOpen) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeChartPicker()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [pickerOpen, closeChartPicker])

  const handleChartClick = useCallback(
    (chartId: ChartId, event: Readonly<PlotMouseEvent>) => {
      if (disabled) return
      const point = event.points[0]
      if (!point) return

      if (chartId === 'severity-distribution') {
        const label = getPointLabel(point)
        if (!label || label === 'No excursions') return
        onChartFiltersChange(
          toggleSeverityChartFilter(chartFilters, label, severityChartLabels(metrics)),
        )
      } else if (chartId === 'impact-by-product') {
        const productName = getPointX(point)
        if (!productName || productName === 'No product data') return
        onChartFiltersChange(
          toggleProductChartFilter(
            chartFilters,
            productName,
            productMetricsChartLabels(metrics),
          ),
        )
      }
    },
    [chartFilters, disabled, metrics, onChartFiltersChange],
  )

  if (metrics.total === 0 && activeCharts.length === 0) return null

  return (
    <div className={cn('flex h-full min-h-0 flex-col', className)}>
      <div className='mb-3 flex shrink-0 items-center justify-between'>
        <div />
        <div className='relative'>
          <button
            type='button'
            disabled={disabled}
            onClick={() => setPickerOpen((open) => !open)}
            aria-expanded={pickerOpen}
            aria-controls='dashboard-chart-picker'
            className={cn(
              'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-medium transition-colors',
              disabled && 'cursor-not-allowed opacity-60',
              pickerOpen
                ? 'border-orange-300 bg-orange-50 text-orange-700'
                : 'border-border bg-white text-muted-foreground hover:bg-black/[0.03] hover:text-foreground',
            )}
          >
            <PlusIcon className='size-3.5' weight='bold' />
            Add / Remove Charts
          </button>

          {pickerOpen && (
            <>
              <div className='fixed inset-0 z-40' onClick={closeChartPicker} role='presentation' />
              <div
                id='dashboard-chart-picker'
                className='absolute right-0 z-50 mt-1.5 w-64 rounded-lg border border-border bg-white p-2 shadow-lg'
              >
                {WORKFLOW_CHART_CATALOG.map((chart) => {
                  const isActive = activeCharts.includes(chart.id)
                  const Icon = chart.icon
                  return (
                    <button
                      key={chart.id}
                      type='button'
                      disabled={disabled}
                      onClick={() => toggleChart(chart.id)}
                      className={cn(
                        'flex w-full items-center gap-2.5 rounded-md px-2.5 py-2 text-left text-sm transition-colors',
                        disabled && 'cursor-not-allowed opacity-60',
                        isActive
                          ? 'bg-orange-50 font-medium text-orange-700'
                          : 'text-muted-foreground hover:bg-black/[0.03] hover:text-foreground',
                      )}
                    >
                      <Icon className='size-4 shrink-0' />
                      <span className='flex-1'>{chart.label}</span>
                      {isActive && <span className='size-2 shrink-0 rounded-full bg-orange-300' />}
                    </button>
                  )
                })}
              </div>
            </>
          )}
        </div>
      </div>

      <div className='min-h-0 flex-1'>
        {activeCharts.length === 0 ? (
          <div className='rounded-lg border border-dashed border-border p-8 text-center text-sm text-muted-foreground'>
            No charts selected. Click &ldquo;Add / Remove Charts&rdquo; to add visualizations.
          </div>
        ) : (
          <div
            className={cn(
              'grid h-full min-h-0 gap-4',
              activeCharts.length === 1 ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2',
            )}
          >
            {activeCharts.map((chartId) => (
              <ChartCard
                key={chartId}
                chartId={chartId}
                metrics={metrics}
                chartFilters={chartFilters}
                onRemove={removeChart}
                onChartClick={handleChartClick}
                disabled={disabled}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

interface SingleDashboardChartProps {
  chartId: ChartId
  metrics: ExcursionMetricsData
  chartFilters?: ChartFilterState
  onChartClick: (chartId: ChartId, event: Readonly<PlotMouseEvent>) => void
  disabled?: boolean
  /** When true, shows the floating remove control (classic dashboard toolbar). */
  showFloatingRemove?: boolean
  onRemove?: (id: ChartId) => void
}

export function SingleDashboardChart({
  chartId,
  metrics,
  chartFilters = EMPTY_CHART_FILTERS,
  onChartClick,
  disabled = false,
  showFloatingRemove = false,
  onRemove,
}: SingleDashboardChartProps) {
  const chart = useMemo(
    () => buildChart(chartId, metrics, chartFilters),
    [chartId, metrics, chartFilters],
  )

  const handleClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => onChartClick(chartId, event),
    [chartId, onChartClick],
  )

  const chartTitle = WORKFLOW_CHART_CATALOG.find((c) => c.id === chartId)?.label ?? chartId
  const { chartContext } = useChatContext()
  const { addChartToChat } = useChatContextActions()
  const isInContext = chartContext?.some((c) => c.chartId === chartId) ?? false

  return (
    <div
      className={cn(
        'group relative flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-border bg-white p-3',
        disabled && 'opacity-90',
      )}
    >
      <div className='absolute right-2 top-2 z-10 flex items-center gap-1'>
        <AddToChatContextButton
          isInContext={isInContext}
          addLabel={`Add ${chartTitle} to chat context`}
          inContextLabel={`${chartTitle} in chat context`}
          onClick={() =>
            addChartToChat([
              {
                report_id: '__dashboard__',
                report_title: 'Yield Excursion Workflow Dashboard',
                section_id: '__dashboard__',
                selection: { chart_id: chartId, chart_title: chartTitle },
              },
            ])
          }
        />
        {showFloatingRemove && onRemove ? (
          <button
            type='button'
            aria-label='Remove chart'
            disabled={disabled}
            onClick={() => onRemove(chartId)}
            className='flex size-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-black/[0.05] hover:text-foreground group-hover:opacity-100'
          >
            <XIcon className='size-3.5' weight='bold' />
          </button>
        ) : null}
      </div>
      {disabled && (
        <div
          className='absolute inset-0 z-10 rounded-lg bg-white/50 backdrop-blur-[1px]'
          aria-hidden
        >
          <div className='h-full w-full animate-pulse bg-black/[0.03]' />
        </div>
      )}
      <div
        className={cn(
          'min-h-0 flex-1 overflow-hidden',
          disabled ? 'cursor-default' : 'cursor-pointer',
          chartId === 'impact-by-product' &&
            !disabled &&
            '[&_.plotly_.barlayer_path]:cursor-pointer [&_.plotly_.barlayer_rect]:cursor-pointer',
        )}
      >
        <ChartRenderer
          data={chart.data}
          layout={chart.layout}
          config={CHART_CONFIG}
          onPlotlyClick={handleClick}
        />
      </div>
    </div>
  )
}

interface ChartCardProps {
  chartId: ChartId
  metrics: ExcursionMetricsData
  chartFilters: ChartFilterState
  onRemove: (id: ChartId) => void
  onChartClick: (chartId: ChartId, event: Readonly<PlotMouseEvent>) => void
  disabled?: boolean
}

function ChartCard({
  chartId,
  metrics,
  chartFilters,
  onRemove,
  onChartClick,
  disabled = false,
}: ChartCardProps) {
  return (
    <SingleDashboardChart
      chartId={chartId}
      metrics={metrics}
      chartFilters={chartFilters}
      onChartClick={onChartClick}
      disabled={disabled}
      showFloatingRemove
      onRemove={onRemove}
    />
  )
}
